export type KnowledgeLevel = 'bronze' | 'silver' | 'gold';

export interface FollowUpItem {
  id: string;
  question: string;
  answer: string;
  nextId: string | null;
}

export interface KnowledgeLevelData {
  title: string;
  content: string;
  funFact: string;
  unlockRequirement: string | null; // achievement ID required, null = unlocked
}

export interface KnowledgeItemV2 {
  id: string;
  targetBody: string | null;
  levels: Record<KnowledgeLevel, KnowledgeLevelData>;
  followUpChain: FollowUpItem[];
}

export const knowledgeDataV2: KnowledgeItemV2[] = [
  {
    id: 'seasons',
    targetBody: 'Earth',
    levels: {
      bronze: {
        title: '地球为什么会有四季？',
        content:
          '地球在绕太阳公转时，地轴倾斜了大约23.5度。这就像你拿着一个手电筒照射一个歪着的球：当北半球歪向太阳时，阳光更集中、更暖和，就是夏天；当北半球歪向远离太阳的方向时，阳光斜斜地照过来、比较分散，就是冬天。南半球的季节正好相反！地球公转一圈需要365天，所以一年有四季更替。',
        funFact: '你知道吗？水星几乎没有四季，因为它的自转轴倾角只有约0.03度，而且公转太快啦！',
        unlockRequirement: null,
      },
      silver: {
        title: '四季形成的深层机制',
        content:
           '四季的本质是太阳直射点在地球表面南北移动。由于地轴倾斜23.5°，太阳赤纬（declination）在一年中从+23.5°（夏至）变化到-23.5°（冬至），春秋分时为0°。直射点处单位面积接收的太阳辐射功率最大，斜射时辐射被分散到更大面积，且大气层路径更长、吸收更多。这就是不同纬度季节差异的物理根源。',
        funFact: '火星的轴倾角约为25.2°，也有四季，但由于轨道偏心率大，南半球冬季比北半球更冷更短。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '米兰科维奇循环与长期气候',
        content:
          '但地球轨道本身也在缓慢变化。地球轨道存在三种周期性变化：偏心率（约10万年周期）、地轴倾角（约4.1万年周期）和岁差（约2.6万年周期），合称米兰科维奇循环。这些参数共同决定了高纬度地区夏季的太阳辐射量，进而驱动冰期-间冰期的旋回。例如，当北半球夏季处于远日点且倾角较小时，高纬度冰雪难以融化，冰期便可能降临。',
        funFact: '由于岁差，约1.2万年后织女星（Vega）将成为新的北极星，而不是现在的勾陈一。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'why-tilt',
        question: '为什么地轴会倾斜23.5度？',
        answer:
          '科学家普遍认为，这是约45亿年前一颗名为“忒伊亚”（Theia）的原行星与原始地球发生巨大碰撞的结果。这次撞击不仅使地轴倾斜，还抛出了大量物质，最终形成了月球。',
        nextId: 'theia',
      },
      {
        id: 'theia',
        question: '忒伊亚撞击对地球还有什么影响？',
        answer:
          '除了产生月球和倾斜地轴，这次撞击还使地球自转速度显著加快（早期一天可能只有6小时），并可能帮助形成了地球的铁核，增强了磁场保护。',
        nextId: 'moon-formation',
      },
      {
        id: 'moon-formation',
        question: '月球形成后如何影响地球？',
        answer:
          '月球的存在稳定了地轴倾角（现在只在22.1°到24.5°之间小幅摆动），使地球气候长期稳定。同时，月球引力引起的潮汐对海洋生物进化、板块运动和地球自转减速都有深远影响。',
        nextId: null,
      },
    ],
  },
  {
    id: 'moon-phases',
    targetBody: 'Moon',
    levels: {
      bronze: {
        title: '月亮为什么每天看起来不一样？',
        content:
          '月球自己不会发光，它像一面大镜子反射太阳的光。月球围绕地球公转，大约27.3天转一圈。我们在地球上看，有时候看到它被太阳照亮的正面（满月），有时候只看到一点点亮边（新月或弯月），有时候完全藏在地球的影子里（月食）。这就是月相变化！',
        funFact: '你看到的月亮总是同一面！因为月球自转和公转周期相同，这叫“潮汐锁定”。',
        unlockRequirement: null,
      },
      silver: {
        title: '月相的数学规律',
        content:
          '月相周期（朔望月）约为29.53天，而月球真正的公转周期（恒星月）约为27.32天。两者的差异是因为地球也在绕太阳公转——月球需要多走一段距离才能重新对准日地连线。月相变化遵循严格的几何关系：从地球上看，太阳与月球的黄经差决定了照亮可见面的比例。满月时两者相差180°，新月时几乎重合。',
        funFact: '中国传统农历就是以朔望月为基础制定的，每月初一为朔（新月），十五为望（满月）。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '潮汐锁定的物理机制',
        content:
          '为什么月球总是用同一面朝向地球？潮汐锁定是引力潮汐摩擦的长期结果。地球对月球的引力在靠近侧和远离侧产生差异（潮汐隆起），月球内部的摩擦会耗散这部分形变能，使其自转逐渐减慢，直到自转周期与公转周期同步。同样的过程也在地球上发生：月球引力使地球自转每世纪减慢约1.7毫秒，日长因此越来越长。',
        funFact: '冥王星和它的卫星卡戎（Charon）是“相互潮汐锁定”的——它们永远只以同一面朝向对方，像一对永恒的舞伴。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'why-tidal-lock',
        question: '所有行星的卫星都会被潮汐锁定吗？',
        answer:
          '理论上，只要时间足够长，几乎所有近距离运行的卫星都会被母行星潮汐锁定。太阳系中，木卫一、木卫二、土卫六等主要卫星都已被潮汐锁定。距离较远或轨道偏心率大的卫星可能需要更长时间。',
        nextId: 'tidal-friction',
      },
      {
        id: 'tidal-friction',
        question: '潮汐摩擦会让月球最终撞上地球吗？',
        answer:
          '恰恰相反！潮汐摩擦导致地月系统的角动量从地球自转向月球轨道转移，月球正以每年约3.8厘米的速度远离地球。数十亿年后，地球的一天将和月球公转周期同步（约47天）。',
        nextId: null,
      },
    ],
  },
  {
    id: 'jupiter-red-spot',
    targetBody: 'Jupiter',
    levels: {
      bronze: {
        title: '木星上的“大红斑”是什么？',
        content:
          '木星的大红斑是一个超级巨大的风暴！它已经吹了至少350年了——比你的爷爷的爷爷的爷爷……还要早得多！这个风暴大到可以装下整个地球。它的颜色来自木星大气中的化学物质，在不同的光照条件下，颜色也会发生变化，有时是深红色，有时偏橙色。',
        funFact: '木星转得特别快，一天只有约10小时！所以大红斑虽然在缩小，但短期内还不会消失。',
        unlockRequirement: null,
      },
      silver: {
        title: '大红斑：一个反气旋风暴的解剖',
        content:
          '大红斑本质上是一个巨大的反气旋（高压系统），位于木星南纬22°附近。其顶部云层温度约-150°C，但深处的热源可能来自木星内部余热。风暴内部存在复杂的有机分子（如红磷化合物），可能是其红色的来源。近几十年的观测表明，大红斑正在以每年约900公里的速度缩小，从19世纪的4万公里宽度降到了如今的约1.5万公里。',
        funFact: '朱诺号探测器发现木星上至少还有12个类似的大大小小的风暴，包括几个白色的“ oval ”风暴，它们也曾合并和变化。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '大红斑与深层射流的卡西尼关系',
        content:
          '但它和木星深层内部有什么关系？卡西尼定律描述了天体自转轴、轨道面和拉普拉斯平面之间的关系。对木星而言，其深层内部存在与表面不同的差速旋转（differential rotation）。朱诺号的引力场测量揭示，大红斑可能向下延伸到数百公里深处，与深层的东向射流（eastward jet）存在动力学耦合。这些射流的能量来源可能与木星内部的热对流和科里奥利力有关。',
        funFact: '木星内部释放的热量比它从太阳接收的还多！这些余热主要来自形成时的收缩，驱动了大气中包括大红斑在内的所有风暴系统。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'why-so-long',
        question: '大红斑为什么能持续数百年？',
        answer:
          '木星没有固体表面，大气摩擦损耗远小于地球。同时，木星内部持续释放的巨大热量为大气运动提供了源源不断的能量，使风暴得以长期维持。此外，周围的大气环流结构也起到了“围护”作用。',
        nextId: 'jupiter-interior',
      },
      {
        id: 'jupiter-interior',
        question: '木星内部到底是什么样子？',
        answer:
          '木星可能有一个由岩石和冰组成的致密核心，质量约为地球的10-20倍。核心外是液态金属氢层，在这里氢呈现出导电的液态金属特性，产生了木星强大的磁场（比地球强14倍）。再向外是液态分子氢层，最外层才是我们观察到的大气。',
        nextId: null,
      },
    ],
  },
  {
    id: 'saturn-rings',
    targetBody: 'Saturn',
    levels: {
      bronze: {
        title: '土星为什么戴着漂亮的“项链”？',
        content:
          '土星的光环其实是由无数块冰块和岩石组成的，它们就像微小的“小月亮”，围绕着土星旋转。这些碎片有的只有沙粒大小，有的像房子一样大！科学家认为，光环可能是被土星强大引力撕碎的小卫星，或者是太阳系早期留下的“建筑材料”碎片。',
        funFact: '土星环虽然宽达28万公里，但厚度只有约1公里！比一张纸还薄得多（相对比例来说）。',
        unlockRequirement: null,
      },
      silver: {
        title: '土星环的结构与动力学',
        content:
          '土星环按发现顺序分为A、B、C环（从内到外），以及更暗的D、E、F、G环。A环和B环之间著名的“卡西尼缝”宽约4800公里，由土卫一（Mimas）的轨道共振清扫而成。A环外缘受“牧羊卫星”土卫十八（Pan）和土卫三十五（Daphnis）的引力约束，形成锋利的边界。环内还存在螺旋密度波，可用于测量土星内部质量分布。',
        funFact: '卡西尼号的最终数据暗示，土星环可能相当“年轻”——可能只有1亿到4亿年历史，在恐龙时代之后才可能形成。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '环的起源与行星形成学',
        content:
          '但它从何而来？土星环位于洛希极限（Roche limit）内，任何靠得太近的卫星都会被土星的潮汐力撕裂。关于环的起源有两种主要假说：（1）一颗约400公里大小的卫星被撕裂；（2）原始太阳星云中未能聚合成卫星的残留物。卡西尼号 finale 数据显示，环的总质量较低（约等于土卫一），成分非常纯净（>99%水冰），这与“古老残留物”假说不太一致，更支持相对年轻的撞击起源。',
        funFact: '如果地球也有类似土星环的环系，从地面看，它们将像一道壮丽的光弧横跨夜空，比满月还要亮！',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'roche-limit',
        question: '洛希极限是怎么计算的？',
        answer:
          '洛希极限d ≈ 2.44 × R × (ρ_M/ρ_m)^(1/3)，其中R是主天体半径，ρ_M和ρ_m分别是主天体和卫星的密度。对土星而言，冰质卫星的洛希极限约在土星赤道上方约14万公里处，正好对应B环的外边界附近。',
        nextId: 'ring-age',
      },
      {
        id: 'ring-age',
        question: '如果土星环很年轻，未来会消失吗？',
        answer:
          '是的！卡西尼号发现，环物质正以“环雨”（ring rain）的形式持续落入土星大气，估计每秒有数百公斤到数吨。按这个速率，土星环可能在1亿到3亿年内完全消失。我们正生活在一个能目睹土星环的幸运时代！',
        nextId: null,
      },
    ],
  },
  {
    id: 'lunar-eclipse',
    targetBody: 'Moon',
    levels: {
      bronze: {
        title: '月食是怎么发生的？',
        content:
          '月食发生时，地球跑到了太阳和月球中间。太阳的光被地球挡住，地球的影子就投射到了月球上。不过月球不会完全变黑——地球大气层会把一部分阳光弯曲并散射到月球表面，这些光中红色光最多，所以月球常常变成暗红色，叫做“血月”！',
        funFact: '月食时，如果你站在月球上，你会看到地球把太阳完全挡住，周围有一圈红色的光环！',
        unlockRequirement: null,
      },
      silver: {
        title: '月食的分类与几何学',
        content:
          '月食分为半影月食、月偏食和月全食三种，取决于月球进入地球阴影的深度。地球本影（umbra）在月球轨道处的直径约为9200公里，远大于月球直径（3474公里），因此月全食时月球可完全没入本影。月食只发生在“食季”——当满月接近黄白交点时。每年通常有2个食季，每个食季约35天。',
        funFact: '月全食的最长理论持续时间约为1小时47分钟——月球必须尽可能穿过本影中心。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '月食在古代天文学中的意义',
        content:
          '但古人是如何利用月食的？月食是证明地球是球体的最早证据之一：地球在本影中投射的影子永远是圆形的，只有球体才能做到这一点。中国古代天文学家通过精确记录月食来检验历法的准确性，《史记》中就有大量月食记载。公元前3世纪，阿里斯塔克斯（Aristarchus）利用月全食时地球本影跨越月球的时间，估算出地月距离约为地球半径的60-80倍，与现代值（约60倍）相当接近。',
        funFact: '阿里斯塔克斯的距离估算误差仅约10%——这在2000多年前是惊人的成就！他还第一个提出了日心说。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'why-red',
        question: '为什么月全食时月亮是红色的，而不是完全黑掉？',
        answer:
          '这是因为地球大气层的瑞利散射。蓝光被散射到四面八方，而波长较长的红光更容易穿透大气层并弯曲（折射）进地球的本影。这些折射后的红光落在月球表面，把它染成暗红色。如果地球没有大气层，月全食时月球将完全不可见。',
        nextId: 'saros-cycle',
      },
      {
        id: 'saros-cycle',
        question: '什么是沙罗周期？',
        answer:
          '沙罗周期约为6585.3天（约18年11天），是朔望月、交点月和近点月的公倍数。经过这样一个周期，日、地、月的相对几何关系几乎完全相同，因此会发生一次非常相似的月食。古代巴比伦天文学家最早发现了这一规律。',
        nextId: null,
      },
    ],
  },
  {
    id: 'sun-nuclear',
    targetBody: 'Sun',
    levels: {
      bronze: {
        title: '太阳为什么会发光发热？',
        content:
          '太阳就像宇宙中的一个超级大锅炉。它的核心温度高达1500万摄氏度，压力大得难以想象。在这种极端条件下，氢原子会发生“核聚变”——4个氢原子合并成1个氦原子，同时释放出巨大的能量。每一秒钟，太阳就把400万吨物质转化成能量！这些能量以光和热的形式传播到太空，给地球带来生命。',
        funFact: '太阳已经燃烧了约46亿年，但它还有约50亿年的燃料！你可以放心地晒太阳。',
        unlockRequirement: null,
      },
      silver: {
        title: '核聚变：质子-质子链反应',
        content:
          '太阳核心的主要能量来源是质子-质子链反应（pp chain）。过程大致为：两个质子聚变成氘，释放正电子和中微子；氘再与一个质子聚变成氦-3；最后两个氦-3聚变成氦-4并释放两个质子。每一步都有质量亏损——约0.7%的初始质量被转化为能量（E=mc²）。太阳每秒将约400万吨氢转化为氦，对应的光度为3.8×10²⁶瓦。',
        funFact: '一个光子从太阳核心走到表面平均需要约17万年！它以随机游走的方式在致密等离子体中艰难前进。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '太阳结构与恒星演化',
        content:
          '但太阳的整体结构和未来命运又是怎样的？太阳从内到外分为：核心（核聚变区，半径约0.25R☉）、辐射区（能量以辐射传递，约0.25-0.7R☉）、对流区（热等离子体对流翻滚，约0.7-1R☉）。可见大气分为光球层（可见光主要来源，约5778K）、色球层（粉红色薄层）和日冕（温度百万度，延伸至行星际空间）。约50亿年后，核心氢耗尽，太阳将膨胀为红巨星，可能吞噬地球。',
        funFact: '太阳的质量占整个太阳系的99.86%！其余所有行星、卫星、小行星和彗星加起来只占0.14%。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'fusion-barrier',
        question: '为什么太阳不会一下子全部炸掉？',
        answer:
          '量子隧穿效应和高温共同作用，使只有极少数质子拥有足够能量克服库仑斥力发生聚变。在太阳核心，一个质子平均要等待约90亿年才能参与一次聚变！这种极低的反应速率反而保证了太阳的稳定燃烧。如果反应太快，核心膨胀降温会自我调节；太慢则会收缩升温——这就是恒星的主序平衡。',
        nextId: 'solar-wind',
      },
      {
        id: 'solar-wind',
        question: '太阳风是什么？对地球有什么影响？',
        answer:
          '太阳风是从日冕持续向外高速喷射的带电粒子流（主要是质子和电子），速度约400-800公里/秒。当太阳风撞击地球磁场时，会被偏转至磁层两侧，形成弓形激波。强烈的太阳风会压缩磁层，引发地磁暴，导致极光增强、卫星故障和电网波动。',
        nextId: null,
      },
    ],
  },
  // ====== 新增：5大行星知识补全 ======
  {
    id: 'mars-red-planet',
    targetBody: 'Mars',
    levels: {
      bronze: {
        title: '火星为什么是红色的？',
        content:
          '火星被称为“红色星球”，因为它的表面覆盖着大量的氧化铁——也就是我们熟悉的铁锈！这些氧化铁来自火星古老的火山活动和岩石风化。火星上还有太阳系最高的火山奥林帕斯山（约21公里高），以及巨大的水手号峡谷。科学家们发现，火星古代曾经有液态水流淌的河道，这意味着它曾经可能更温暖、更湿润。',
        funFact: '火星上的日落是蓝色的！因为稀薄的火星大气对蓝光的散射方式与地球不同。',
        unlockRequirement: null,
      },
      silver: {
        title: '火星的地质历史与气候变迁',
        content:
          '火星的地质历史可以分为三个主要时期：诺亚纪（大量液态水存在）、赫斯珀里亚纪（火山活动频繁）和亚马逊纪（寒冷干燥至今）。轨道探测器发现了古老的河床、三角洲和湖泊沉积物。火星极地冰盖由水冰和干冰（固态二氧化碳）组成，随季节变化而消长。为什么火星从湿润走向干燥？可能因为它缺乏全球磁场，太阳风逐渐剥离了大气层。',
        funFact: '毅力号火星车正在采集岩石样本，计划未来由后续任务带回地球——这将是人类首次从火星带回样本！',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '火星宜居性与载人探索前景',
        content:
          '但它未来能成为人类的新家园吗？火星的宜居性评估涉及多个因素：表面辐射强度高（缺乏磁场保护）、大气稀薄（仅为地球的0.6%）、温度极低（平均-60°C）。但地下可能存在液态卤水，两极有水冰资源。未来的载人火星任务需要解决辐射防护、生命支持系统和原位资源利用（ISRU）等关键技术。SpaceX的星舰、NASA的阿尔忒弥斯计划都将火星作为远期目标。',
        funFact: '如果你在火星上跳高，你可以跳到地球上的约2.5倍高——因为火星的引力只有地球的38%！',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'mars-water',
        question: '火星上的水都去哪了？',
        answer:
          '大部分水被锁在极地冰盖和地下永久冻土中，一部分可能被太阳风吹散到太空。火星缺乏全球磁场，无法像地球一样阻挡太阳风对大气的剥离。',
        nextId: 'mars-life',
      },
      {
        id: 'mars-life',
        question: '火星上可能存在过生命吗？',
        answer:
          '目前尚未发现确凿的生命证据，但火星陨石ALH 84001中发现的有机分子和类似微生物的结构曾引发激烈争论。毅力号正在耶泽罗陨石坑寻找古代微生物生命的痕迹。',
        nextId: null,
      },
    ],
  },
  {
    id: 'venus-greenhouse',
    targetBody: 'Venus',
    levels: {
      bronze: {
        title: '金星为什么是最热的行星？',
        content:
          '虽然金星不是离太阳最近的行星，但它是太阳系中最热的行星——表面温度高达462°C，足以融化铅！这是因为金星有一层极厚的大气层，其中96%是二氧化碳。二氧化碳像一床厚厚的棉被，让太阳光进入却不让热量散出，这种现象叫做“温室效应”。金星的温室效应失控了，比地球强得多。更奇怪的是，金星是逆向自转的——太阳从西边升起，东边落下！',
        funFact: '金星上的一天比一年还长！金星自转一圈需要243个地球日，而公转一圈只需要225个地球日。',
        unlockRequirement: null,
      },
      silver: {
        title: '失控温室效应与硫酸云',
        content:
          '金星大气层厚约250公里，表面气压是地球的92倍——相当于在地球海洋900米深处的压力。大气中96%是二氧化碳，3.5%是氮气，还有微量的水蒸气和二氧化硫。高层大气中的二氧化硫与水反应形成浓硫酸云，这些云反射了约75%的入射阳光，使金星表面本应比地球更暗。但温室效应仍然使表面温度极高，因为可见光进入后被表面吸收，而红外辐射被二氧化碳阻挡无法逃逸。',
        funFact: '麦哲伦号雷达测绘发现金星表面有数千座火山，其中一些可能至今仍在活动。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '金星与地球：相似却不同的命运',
        content:
          '但为什么它和地球如此相似却走向不同命运？金星的质量、半径和密度都与地球非常接近，被称为“地球的姐妹星”。但金星的板块构造（如果存在）非常不活跃，无法像地球一样通过火山喷发和板块运动回收二氧化碳。长期积累的二氧化碳最终导致失控温室效应。有趣的是，一些科学家认为金星在古代（约30亿年前）可能曾有液态海洋和适宜气候。这一假说被称为“古代金星宜居性”，是未来探测的重要科学目标。',
        funFact: '前苏联的金星号探测器是唯一成功在金星表面着陆并传回数据的航天器，但在极端环境下最长只工作了约2小时。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'venus-day',
        question: '金星上的一天有多奇怪？',
        answer:
          '金星自转非常缓慢且方向与大多数行星相反（逆向自转）。一个金星日（243地球日）比一个金星年（225地球日）还长。这意味着如果你在金星上，你会先看到太阳从西边升起，然后它会在天空中慢慢移动，最终从东边落下——整个过程可能需要数月！',
        nextId: 'venus-habitable',
      },
      {
        id: 'venus-habitable',
        question: '金星曾经宜居吗？',
        answer:
          '一些气候模型表明，约30亿年前的金星可能拥有液态海洋和温和的气候。但太阳亮度逐渐增加，加上金星缺乏有效碳循环，最终导致海洋蒸发、水分子被紫外线分解，氢逃逸到太空，留下浓厚的二氧化碳大气。这一“失控温室效应”的演化路径，也警示我们保护地球气候的重要性。',
        nextId: null,
      },
    ],
  },
  {
    id: 'mercury-extreme',
    targetBody: 'Mercury',
    levels: {
      bronze: {
        title: '水星：离太阳最近的信使',
        content:
          '水星是太阳系中最小、离太阳最近的行星。它绕太阳公转一圈只需要88个地球日——也就是说，水星上一年只有88天！但水星的一天却非常漫长：由于特殊的3:2自转-公转共振，水星自转三圈的同时公转两圈，一个完整的“日-夜周期”（太阳两次过中天）需要176个地球日。水星表面温差极其极端：白天温度可达427°C，夜晚降至-173°C，温差超过600°C！',
        funFact: '水星的名字来自罗马神话中的信使之神墨丘利，因为它在天空中移动得最快。',
        unlockRequirement: null,
      },
      silver: {
        title: '水星共振与极地冰层',
        content:
          '令人惊讶的是，水星的极地陨石坑里竟然可能有冰！水星几乎没有大气层来保持热量或阻挡陨石，但极地某些陨石坑的底部永远处于阴影中，温度可低至-180°C。信使号（MESSENGER）探测器在这些永久阴影区探测到了水冰和有机物的雷达信号。这些冰可能来自彗星或小行星撞击带来的水。同时，水星的3:2自转-公转共振意味着在某些经度上，太阳会升起、落下、再升起、再落下——在同一个“白天”内！',
        funFact: '水星表面看起来很像月球——布满陨石坑、平原和悬崖。这些悬崖（皱脊）可能是水星早期冷却收缩时地壳断裂形成的。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '水星近日点进动与广义相对论',
        content:
          '但水星还有一个更深刻的秘密：它的轨道在缓慢旋转。水星椭圆的轨道长轴（近日点方向）每世纪会额外进动约43角秒——这个微小偏差无法用牛顿力学完全解释。1915年，爱因斯坦用广义相对论精确地解释了这个现象：太阳的巨大质量弯曲了周围的时空，水星在这个弯曲时空中运动，轨道自然会发生进动。水星近日点进动成为广义相对论最早的实验验证之一，也是科学史上理论与观测完美结合的经典案例。',
        funFact: '水星是太阳系中除地球外唯一拥有全球磁场的类地行星，但它的磁场非常弱——只有地球的约1%。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'mercury-ice',
        question: '水星离太阳那么近，怎么会有冰？',
        answer:
          '水星极地某些陨石坑的坑底永远被陨石坑壁遮挡，阳光从未照射到那里。这些永久阴影区的温度可低至-180°C，足以让水冰稳定存在。信使号探测器通过中子谱仪和激光高度计确认了这些冰层的存在。',
        nextId: 'mercury-resonance',
      },
      {
        id: 'mercury-resonance',
        question: '什么是3:2自转-公转共振？',
        answer:
          '这意味着水星每绕太阳公转2圈，恰好自转3圈。这种特殊比例是太阳潮汐力长期作用的结果。由于这个共振，水星表面的某些区域会在近日点附近正对着太阳，受到特别强烈的加热——这也是水星表面出现“热点”的原因。',
        nextId: null,
      },
    ],
  },
  {
    id: 'uranus-tilted',
    targetBody: 'Uranus',
    levels: {
      bronze: {
        title: '天王星：一颗“躺倒”的行星',
        content:
          '天王星是太阳系中最“懒”的行星——它的自转轴倾斜了约98度，几乎是“躺倒”着绕太阳公转的！这意味着天王星的季节极为极端：每个极区会经历长达42年的连续白天，然后是42年的连续黑夜。天王星是一颗“冰巨星”，大气中充满甲烷，吸收了红光，所以看起来是美丽的蓝绿色。它也有环系，但比土星的环暗得多、细得多。',
        funFact: '天王星是第一颗用望远镜发现的行星。1781年，英国天文学家威廉·赫歇尔在自家花园里发现了它。',
        unlockRequirement: null,
      },
      silver: {
        title: '天王星的极端季节与神秘磁场',
        content:
          '由于98°的轴倾角，在至点时，天王星的南极或北极会直接朝向太阳，而赤道地区则陷入长达42年的黑暗。天王星的磁场也非常奇特：它的磁轴与自转轴夹角约59度，而且磁场中心偏离行星中心约1/3个半径。这与地球、木星等行星的对称磁场截然不同。科学家推测，这种不规则磁场可能与天王星内部的水-氨-甲烷“海洋”中的对流运动有关。',
        funFact: '旅行者2号是唯一访问过天王星的航天器，它在1986年飞掠时只看到了一片几乎没有特征的蓝绿色星球——因为那时正好处于昼夜平分点附近。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '冰巨星的内部与起源',
        content:
          '但为什么它会“躺倒”？主流假说认为，在太阳系早期，天王星曾遭受一颗地球大小的原行星的巨大撞击，这次撞击不仅使天王星倾斜，还可能抛出了大量物质形成了它的卫星系统。天王星的内部结构也不同于气态巨行星：它可能有一个岩石-冰核心，外面包裹着一层由水、甲烷和氨组成的高温高压“海洋”（超临界流体），再外面才是氢和氦的大气层。这就是为什么它被称为“冰巨星”——尽管内部温度高达数千度。',
        funFact: '天王星的大气中检测到硫化氢，闻起来像臭鸡蛋的味道！如果真的有宇航员飞过上空，可能会闻到一股“臭鸡蛋味”。',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'uranus-tilt-cause',
        question: '天王星为什么会“躺倒”？',
        answer:
          '最被广泛接受的假说是：早期太阳系中，天王星曾与一颗地球大小的原行星发生巨大撞击。这次撞击不仅使天王星自转轴倾斜了约98度，还可能导致它失去了大部分原始大气，并抛出了形成其卫星系统的物质。',
        nextId: 'uranus-moons',
      },
      {
        id: 'uranus-moons',
        question: '天王星的卫星有什么特别？',
        answer:
          '天王星有27颗已知卫星，它们的名字都取自莎士比亚和蒲柏作品中的人物（如泰坦尼亚、奥伯龙、米兰达）。米兰达表面有太阳系中最高的悬崖之一（约20公里高），可能是早期被撞击碎裂后重新聚合的结果。',
        nextId: null,
      },
    ],
  },
  {
    id: 'neptune-math',
    targetBody: 'Neptune',
    levels: {
      bronze: {
        title: '海王星：数学预言的蓝色世界',
        content:
          '海王星是太阳系中唯一一颗先被数学家“算”出来、然后才用望远镜找到的行星！19世纪，天文学家发现天王星的轨道与牛顿力学预测有微小偏差。法国数学家勒维耶和英国数学家亚当斯几乎同时预测：一定存在一颗未知行星在拉扯天王星。1846年，德国天文学家伽勒在勒维耶预测的位置附近果然发现了海王星。海王星是太阳系中风最强的行星，风速可达每小时2100公里，比地球上的飓风快得多！',
        funFact: '海王星上有一个和木星大红斑类似的大暗斑，但它时隐时现——1989年旅行者2号看到了它，但1994年哈勃望远镜观测时它却消失了。',
        unlockRequirement: null,
      },
      silver: {
        title: '海王星的发现与大气动力学',
        content:
          '勒维耶仅用笔尖和纸张，根据天王星轨道的微小扰动，就精确预测了海王星的位置（误差仅约1度）。这是科学史上最伟大的理论胜利之一。海王星的大气以氢和氦为主，还含有更多的甲烷，这使它比天王星呈现更深的蓝色。海王星的大暗斑（Great Dark Spot）是一个与木星大红斑类似的反气旋风暴，直径约13000公里。但不同于大红斑的持续存在，大暗斑在1989年被旅行者2号观测到后，到1994年哈勃望远镜再看时已经消失了——取而代之的是新的风暴系统。',
        funFact: '海王星的卫星海卫一（Triton）是太阳系中唯一一颗逆行轨道的大卫星，科学家认为它原本是一颗柯伊伯带天体，被海王星引力捕获。',
        unlockRequirement: 'bronze_scholar',
      },
      gold: {
        title: '冰巨星的热源与内部结构',
        content:
          '但它内部又隐藏着什么秘密？海王星和天王星被称为“冰巨星”，但它们的内部并非冰冷。海王星有一个岩石-冰核心，外面包裹着水-氨-甲烷超临界流体层，最外层是氢氦大气。奇怪的是，尽管海王星离太阳更远，它释放的内部热量却比天王星多得多——它从内部释放的能量是从太阳接收到的2.6倍。这种额外热源可能来自形成时的引力收缩、核心结晶释放的潜热，或内部流体摩擦。海卫一的冰火山向太空喷射氮气冰和尘埃，是太阳系中最活跃的地质体之一。',
        funFact: '海王星内部的高温高压环境可能产生“钻石雨”——碳原子在甲烷中凝结成钻石，像雨一样落向核心！',
        unlockRequirement: 'silver_scholar',
      },
    },
    followUpChain: [
      {
        id: 'neptune-discovery',
        question: '勒维耶是怎么“算”出海王星的？',
        answer:
          '勒维耶利用牛顿万有引力定律和天体力学，反推天王星轨道扰动的来源。他假设存在一颗未知行星，通过迭代计算确定了它的质量、轨道和位置。1846年9月，他将预测位置寄给柏林天文台的伽勒。伽勒在收到信后的当晚，就在预测位置附近1度范围内发现了海王星。',
        nextId: 'neptune-triton',
      },
      {
        id: 'neptune-triton',
        question: '海卫一为什么会逆行？',
        answer:
          '海卫一的逆行轨道表明它不是和海王星同时形成的，而是后来被捕获的柯伊伯带天体。捕获过程可能使海卫一进入了一个高度倾斜的逆行轨道，并引发了海王星其他原始卫星的碰撞和重组。海卫一还是太阳系中最冷的天体之一，表面温度约-235°C，但地下可能有液态水海洋。',
        nextId: null,
      },
    ],
  },
];

export function getKnowledgeForBody(bodyId: string): KnowledgeItemV2 | undefined {
  return knowledgeDataV2.find(
    (k) => k.targetBody?.toLowerCase() === bodyId.toLowerCase()
  );
}

export function getKnowledgeById(id: string): KnowledgeItemV2 | undefined {
  return knowledgeDataV2.find((k) => k.id === id);
}
